import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { API_ORIGIN } from '../constants'
import { buildEmployerJobsApiSearchParams } from '@/lib/list-filters/employer-jobs'
import type { PaginatedListMeta } from '@/lib/list-pagination'
import type { EntitlementDeniedPayload } from '@/components/gating/types'
import { parseEntitlementDeniedFromResponse } from '@/components/gating/parse-entitlement-denied'
import { featureLabelFromConfig } from '@/components/gating/gating-copy'
import { getUpgradePlanConfig, resolveUpgradePlanPath } from '../constants/upgrade-plan'

export type OpportunityMutationError = Error & {
  upgradePath?: string
  denial?: EntitlementDeniedPayload
}

async function throwOpportunityApiError(response: Response, fallback: string): Promise<never> {
  const error = await response.json().catch(() => ({}))
  const denial = parseEntitlementDeniedFromResponse(response.status, error)
  if (denial) {
    const upgrade = getUpgradePlanConfig()
    const featureLabel = featureLabelFromConfig(denial.feature_key)
    const err = new Error(
      denial.reason ||
        `${featureLabel} is unavailable on your current plan. Open ${upgrade?.label ?? 'Plan & Billing'} to upgrade.`,
    ) as OpportunityMutationError
    err.upgradePath =
      denial.upgrade_path ||
      resolveUpgradePlanPath(
        typeof denial.organisation_slug === 'string' ? denial.organisation_slug : null,
      )
    err.denial = denial
    throw err
  }
  throw new Error(error.detail || error.error || fallback)
}

export interface OrganisationOpportunity {
  id: string
  title: string
  description: string
  skills: OrganisationOpportunitySkill[]
  experiences: OrganisationOpportunityExperience[]
  created_at: string
  updated_at: string
  publication_status?: 'draft' | 'published' | 'closed' | 'archived'
  public_slug?: string | null
  published_at?: string | null
  organisation_logo_url?: string | null
  organisation_name?: string
  organisation_slug?: string
  employment_type?: string
  work_mode?: string
  city?: string
  country_code?: string
  salary_visibility?: string
  salary_min?: number | null
  salary_max?: number | null
  salary_currency?: string
  salary_period?: string
  /** Denormalised string (search / display); server syncs from structured fields. */
  location?: string
  is_featured?: boolean
  featured_at?: string | null
  featured_until?: string | null
  featured_is_active?: boolean
}

export interface OrganisationOpportunitySkill {
  id: string
  /** Catalog Skill.id (API `skill_id`). */
  skill_id?: string
  skill_name: string
  requirement_type: string
}

export interface OrganisationOpportunityExperience {
  id: string
  description: string
}

export interface OrganisationOpportunitySkillData {
  skill_id: string
  requirement_type: 'required' | 'preferred'
}

export interface CreateOrganisationOpportunityData {
  title: string
  description: string
  skills: OrganisationOpportunitySkillData[]
  employment_type?: string
  work_mode?: string
  city?: string
  country_code?: string
  salary_visibility?: string
  salary_min?: number | null
  salary_max?: number | null
  salary_currency?: string
  salary_period?: string
  is_featured?: boolean
}

export interface UpdateOrganisationOpportunityData {
  title: string
  description: string
  /** When set, replaces all skills (empty array clears). Omit to leave skills unchanged. */
  skills?: OrganisationOpportunitySkillData[]
  employment_type?: string
  work_mode?: string
  city?: string
  country_code?: string
  salary_visibility?: string
  salary_min?: number | null
  salary_max?: number | null
  salary_currency?: string
  salary_period?: string
  is_featured?: boolean
}

/** Re-send structured fields unchanged when updating only title/description/skills via PUT. */
export function managedOpportunityStructuredFields(
  opportunity: OrganisationOpportunity,
): Pick<
  UpdateOrganisationOpportunityData,
  | 'employment_type'
  | 'work_mode'
  | 'city'
  | 'country_code'
  | 'salary_visibility'
  | 'salary_min'
  | 'salary_max'
  | 'salary_currency'
  | 'salary_period'
  | 'is_featured'
> {
  const salaryVisibility = opportunity.salary_visibility ?? 'not_disclosed'
  const salaryCurrency = opportunity.salary_currency?.trim().toUpperCase()
  return {
    employment_type: opportunity.employment_type ?? '',
    work_mode: opportunity.work_mode ?? '',
    city: opportunity.city ?? '',
    country_code: opportunity.country_code ?? '',
    salary_visibility: salaryVisibility,
    salary_min: opportunity.salary_min ?? null,
    salary_max: opportunity.salary_max ?? null,
    salary_currency: salaryVisibility === 'shown' ? salaryCurrency || 'AUD' : salaryCurrency || '',
    salary_period: opportunity.salary_period ?? '',
    is_featured: Boolean(opportunity.is_featured),
  }
}

export interface PaginatedOrganisationOpportunities {
  results: OrganisationOpportunity[]
  page: number
  page_size: number
  total: number
  has_next: boolean
}

export type ManagedOrganisationOpportunitiesQuery = {
  filters?: import('@/lib/list-filters/employer-jobs').EmployerJobListFilters
  page?: number
  pageSize?: number
  /** When false, omit page params and return the full list (legacy merge-all path). */
  paginate?: boolean
}

function requireOrgSlug(organisationSlug: string): void {
  if (!organisationSlug?.trim()) {
    throw new Error('Missing organisation: open Job Openings after your organisations have loaded, or create an organisation first.')
  }
}

export async function fetchManagedOrganisationOpportunities(
  organisationSlug: string,
  token: string | null,
  query: ManagedOrganisationOpportunitiesQuery = {},
): Promise<PaginatedOrganisationOpportunities> {
  requireOrgSlug(organisationSlug)
  const params =
    query.filters && query.paginate !== false
      ? buildEmployerJobsApiSearchParams(query.filters, {
          page: query.page ?? query.filters.page,
          pageSize: query.pageSize ?? query.filters.page_size,
          includePagination: true,
        })
      : new URLSearchParams()
  const qs = params.toString()
  const response = await fetch(
    `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(organisationSlug.trim())}/opportunities${qs ? `?${qs}` : ''}`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch opportunities')
  }
  return (await response.json()) as PaginatedOrganisationOpportunities
}

export async function fetchManagedOrganisationOpportunity(
  organisationSlug: string,
  opportunityId: string,
  token: string | null,
): Promise<OrganisationOpportunity> {
  requireOrgSlug(organisationSlug)
  const response = await fetch(
    `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(organisationSlug.trim())}/opportunities/${encodeURIComponent(opportunityId)}/`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch opportunity')
  }
  return response.json()
}

export function useManagedOrganisationOpportunity(organisationSlug: string, opportunityId: string) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['organisation-opportunity', organisationSlug, opportunityId],
    queryFn: () => fetchManagedOrganisationOpportunity(organisationSlug, opportunityId, token),
    enabled: !!token && !!organisationSlug?.trim() && !!opportunityId,
  })
}

export async function fetchOpportunity(
  opportunityId: string,
  token: string | null,
): Promise<OrganisationOpportunity> {
  const response = await fetch(
    `${API_ORIGIN}/api/opportunities/managed/${encodeURIComponent(opportunityId)}/`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new Error('Failed to fetch opportunity')
  }
  return response.json()
}

export async function updateOpportunityById(
  opportunityId: string,
  data: UpdateOrganisationOpportunityData,
  token: string | null,
): Promise<OrganisationOpportunity> {
  const response = await fetch(
    `${API_ORIGIN}/api/opportunities/managed/${encodeURIComponent(opportunityId)}/`,
    {
      method: 'PUT',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  )
  if (!response.ok) {
    await throwOpportunityApiError(response, 'Failed to update opportunity')
  }
  return response.json()
}

export async function deleteOpportunityById(
  opportunityId: string,
  token: string | null,
): Promise<void> {
  const response = await fetch(
    `${API_ORIGIN}/api/opportunities/managed/${encodeURIComponent(opportunityId)}/`,
    {
      method: 'DELETE',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || error.error || 'Failed to delete opportunity')
  }
}

export function useOpportunity(opportunityId: string | undefined) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => fetchOpportunity(opportunityId!, token),
    enabled: !!token && !!opportunityId,
  })
}

export function useOpportunityMutations(opportunityId: string | undefined) {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateOrganisationOpportunityData) =>
      updateOpportunityById(opportunityId!, data, token),
    onSuccess: (updated: OrganisationOpportunity) => {
      if (opportunityId) {
        queryClient.setQueryData(['opportunity', opportunityId], updated)
      }
      const orgSlug = updated.organisation_slug?.trim()
      if (orgSlug) {
        queryClient.invalidateQueries({ queryKey: ['organisation-opportunities', orgSlug] })
        queryClient.setQueryData(['organisation-opportunity', orgSlug, updated.id], updated)
      }
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => deleteOpportunityById(opportunityId!, token),
    onSuccess: () => {
      if (opportunityId) {
        queryClient.removeQueries({ queryKey: ['opportunity', opportunityId] })
      }
      void queryClient.invalidateQueries({ queryKey: ['organisation-opportunities'] })
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })

  return {
    updateOpportunity: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    deleteOpportunity: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
  }
}

export function useOrganisationOpportunities(
  organisationSlug: string,
  query: ManagedOrganisationOpportunitiesQuery = {},
) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const filters = query.filters
  const listKey = filters
    ? [
        'organisation-opportunities',
        organisationSlug,
        filters.page,
        filters.page_size,
        filters.q,
        filters.q_operator,
        filters.publication_status.join(','),
        filters.publication_status_operator,
        filters.employment_type.join(','),
        filters.employment_type_operator,
        filters.work_mode.join(','),
        filters.work_mode_operator,
        filters.sort,
      ]
    : (['organisation-opportunities', organisationSlug] as const)

  const opportunitiesQuery = useQuery({
    queryKey: listKey,
    queryFn: () => fetchManagedOrganisationOpportunities(organisationSlug, token, query),
    enabled: !!token && !!organisationSlug,
  })

  const createOpportunityMutation = useMutation({
    mutationFn: async (data: CreateOrganisationOpportunityData): Promise<OrganisationOpportunity> => {
      requireOrgSlug(organisationSlug)
      const response = await fetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(organisationSlug.trim())}/opportunities/`,
        {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        await throwOpportunityApiError(response, 'Failed to create opportunity')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisation-opportunities', organisationSlug] })
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })

  const updateOpportunityMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOrganisationOpportunityData }): Promise<OrganisationOpportunity> => {
      requireOrgSlug(organisationSlug)
      const response = await fetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(organisationSlug.trim())}/opportunities/${encodeURIComponent(id)}/`,
        {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        await throwOpportunityApiError(response, 'Failed to update opportunity')
      }
      return response.json()
    },
    onSuccess: (updated: OrganisationOpportunity, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organisation-opportunities', organisationSlug] })
      queryClient.setQueryData(['organisation-opportunity', organisationSlug, variables.id], updated)
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })

  const deleteOpportunityMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      requireOrgSlug(organisationSlug)
      const response = await fetch(
        `${API_ORIGIN}/api/organisations/managed/${encodeURIComponent(organisationSlug.trim())}/opportunities/${encodeURIComponent(id)}/`,
        {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to delete opportunity')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisation-opportunities', organisationSlug] })
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })

  const pagination: PaginatedListMeta | undefined = opportunitiesQuery.data
    ? {
        page: opportunitiesQuery.data.page,
        page_size: opportunitiesQuery.data.page_size,
        total: opportunitiesQuery.data.total,
        has_next: opportunitiesQuery.data.has_next,
      }
    : undefined

  return {
    opportunities: opportunitiesQuery.data?.results ?? [],
    pagination,
    isLoading: opportunitiesQuery.isLoading,
    error: opportunitiesQuery.error,
    createOpportunity: createOpportunityMutation.mutateAsync,
    isCreating: createOpportunityMutation.isPending,
    createError: createOpportunityMutation.error,
    updateOpportunity: updateOpportunityMutation.mutateAsync,
    isUpdating: updateOpportunityMutation.isPending,
    updateError: updateOpportunityMutation.error,
    deleteOpportunity: deleteOpportunityMutation.mutateAsync,
    isDeleting: deleteOpportunityMutation.isPending,
    deleteError: deleteOpportunityMutation.error,
  }
}
